/**
 * CallAttendPage — /calls/:callSlug/attend/:value
 *
 * Landing target for the one-click attendance buttons in a call reminder
 * email (Call Asks, BN#2935/PR#1470). The email link is a plain top-level GET
 * so link-scanners never trigger it — the actual write happens from THIS
 * page's own client-side fetch on mount, which no scanner executes. See
 * worker/src/calls-asks.ts for the response rules and worker/src/
 * calls-reminders.ts's buildAskButtonsBlock for how the link is built.
 */

import { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import TakerShell from './components/TakerShell';

const API_BASE = (import.meta as unknown as { env: Record<string, string> }).env?.VITE_API_BASE_URL ?? '';

type PageState =
  | { phase: 'loading' }
  | { phase: 'done'; value: 'live' | 'recording'; firstName: string | null }
  | { phase: 'error'; message: string };

export default function CallAttendPage() {
  const { callSlug, value } = useParams<{ callSlug: string; value: string }>();
  const [searchParams] = useSearchParams();
  const [state, setState] = useState<PageState>({ phase: 'loading' });

  useEffect(() => {
    const t = searchParams.get('t') ?? '';
    if (!callSlug || (value !== 'live' && value !== 'recording') || !t) {
      setState({ phase: 'error', message: 'This link is incomplete.' });
      return;
    }
    fetch(`${API_BASE}/api/public/calls/${encodeURIComponent(callSlug)}/attend`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ t, value }),
    })
      .then(async (r) => {
        const d = await r.json().catch(() => ({})) as { ok?: boolean; error?: string; firstName?: string | null };
        if (!r.ok || !d.ok) {
          setState({ phase: 'error', message: d.error || 'This link has expired.' });
          return;
        }
        setState({ phase: 'done', value: value as 'live' | 'recording', firstName: d.firstName ?? null });
      })
      .catch(() => setState({ phase: 'error', message: 'Something went wrong — please try the link again.' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callSlug, value]);

  const callPagePath = callSlug ? `/calls/${encodeURIComponent(callSlug)}` : '/';

  return (
    <TakerShell>
      <div style={{
        minHeight: '55vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '48px 24px', fontFamily: "'Poppins', sans-serif", textAlign: 'center',
      }}>
        {state.phase === 'loading' && <div style={{ color: '#6b7280', fontSize: 14 }}>One moment…</div>}

        {state.phase === 'error' && (
          <div style={{ maxWidth: 440 }}>
            <p style={{ color: '#991b1b', fontSize: 15, marginBottom: 18 }}>{state.message}</p>
            <Link to={callPagePath} style={{ color: '#F05A28', fontWeight: 600, fontSize: 14, textDecoration: 'none' }}>
              ← Back to the call page
            </Link>
          </div>
        )}

        {state.phase === 'done' && (
          <div style={{ maxWidth: 440 }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%', background: '#FFE4D6', color: '#F05A28',
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px',
              fontSize: 26, fontWeight: 700,
            }}>✓</div>
            <h1 style={{ fontFamily: "'Fraunces', serif", fontSize: 26, fontWeight: 800, color: '#0A0A2E', marginBottom: 10 }}>
              {state.value === 'live'
                ? `Got it${state.firstName ? `, ${state.firstName}` : ''} — see you live.`
                : `Got it${state.firstName ? `, ${state.firstName}` : ''} — we'll send the recording your way.`}
            </h1>
            <p style={{ fontSize: 15, color: '#4b5563', lineHeight: 1.6, marginBottom: 24 }}>
              {state.value === 'live'
                ? 'The Zoom join button appears on the call page 15 minutes before it starts.'
                : "No need to clear your calendar — we'll let you know the moment it's ready to watch."}
            </p>
            <Link to={callPagePath} style={{ color: '#F05A28', fontWeight: 600, fontSize: 14, textDecoration: 'none' }}>
              ← Back to the call page
            </Link>
          </div>
        )}
      </div>
    </TakerShell>
  );
}
